import React from 'react'
import { Box, BoxProps, styled } from '@mui/material'
import { Close } from '@mui/icons-material'

/**
 * Persistent, in-flow alert banner — the counterpart to `Toast` for
 * anything that should stay visible after the user looks away and back
 * (form errors, danger-zone warnings, plan-gated notices). Same four
 * meanings and icon language as `Toast`.
 */

export type AlertVariant = 'success' | 'error' | 'warning' | 'info'

const GLYPH: Record<AlertVariant, string> = {
  success: '✓',
  error: '✕',
  warning: '!',
  info: 'i',
}

export interface AlertAction {
  label: string
  onClick: () => void
}

export interface AlertProps extends Omit<BoxProps, 'children' | 'title'> {
  variant: AlertVariant
  title: React.ReactNode
  description?: React.ReactNode
  /** Optional inline text links rendered under the description. */
  actions?: AlertAction[]
  /** Show a dismiss (×) control. */
  onDismiss?: () => void
  /** Override the default variant glyph. */
  icon?: React.ReactNode
}

const Root = styled(Box, { shouldForwardProp: (p) => p !== 'variant' })<{ variant: AlertVariant }>(
  ({ variant }) => ({
    display: 'flex',
    gap: 13,
    alignItems: 'flex-start',
    padding: '15px 16px',
    borderRadius: 'var(--ds-radius-md)',
    border: '1.5px solid',
    ...(variant === 'success' && {
      background: 'var(--ds-alert-ok-soft)',
      borderColor: 'var(--ds-alert-ok-line)',
    }),
    ...(variant === 'error' && {
      background: 'var(--ds-alert-err-soft)',
      borderColor: 'var(--ds-alert-err-line)',
    }),
    ...(variant === 'warning' && {
      background: 'var(--ds-alert-warn-soft)',
      borderColor: 'var(--ds-alert-warn-line)',
    }),
    ...(variant === 'info' && {
      background: 'var(--ds-alert-info-soft)',
      borderColor: 'var(--ds-alert-info-line)',
    }),
  }),
)

const IconChip = styled('span', { shouldForwardProp: (p) => p !== 'variant' })<{ variant: AlertVariant }>(
  ({ variant }) => ({
    width: 30,
    height: 30,
    borderRadius: 9,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: 13,
    fontWeight: 700,
    flexShrink: 0,
    color: '#fff',
    // Spectrum hues are light — dark ink on every chip for contrast.
    ...(variant === 'success' && { background: 'var(--ds-alert-ok)', color: 'var(--ds-alert-ok-ink)' }),
    ...(variant === 'error' && { background: 'var(--ds-alert-err)', color: 'var(--ds-alert-err-ink)' }),
    ...(variant === 'warning' && { background: 'var(--ds-alert-warn)', color: 'var(--ds-alert-warn-ink)' }),
    ...(variant === 'info' && { background: 'var(--ds-alert-info)', color: 'var(--ds-alert-info-ink)' }),
  }),
)

const inkFor: Record<AlertVariant, string> = {
  success: 'var(--ds-alert-ok-ink)',
  error: 'var(--ds-alert-err-ink)',
  warning: 'var(--ds-alert-warn-ink)',
  info: 'var(--ds-alert-info-ink)',
}

/** Success/error/warning/info banner — see `AlertProps`. */
export function Alert({
  variant,
  title,
  description,
  actions,
  onDismiss,
  icon,
  ...rest
}: AlertProps) {
  return (
    <Root variant={variant} role={variant === 'error' ? 'alert' : 'status'} {...rest}>
      <IconChip variant={variant}>{icon ?? GLYPH[variant]}</IconChip>
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Box sx={{ fontSize: 13.5, fontWeight: 680, color: 'var(--ds-ink)' }}>{title}</Box>
        {description && (
          <Box sx={{ fontSize: 12.5, color: 'var(--ds-ink-muted)', lineHeight: 1.55, mt: 0.4 }}>
            {description}
          </Box>
        )}
        {actions && actions.length > 0 && (
          <Box sx={{ display: 'flex', gap: 2, mt: 1 }}>
            {actions.map((a) => (
              <Box
                key={a.label}
                component='a'
                onClick={a.onClick}
                sx={{
                  fontSize: 12.5,
                  fontWeight: 640,
                  textDecoration: 'underline',
                  cursor: 'pointer',
                  color: inkFor[variant],
                }}
              >
                {a.label}
              </Box>
            ))}
          </Box>
        )}
      </Box>
      {onDismiss && (
        <Box
          component='button'
          onClick={onDismiss}
          aria-label='Dismiss'
          sx={{
            marginLeft: '6px',
            flexShrink: 0,
            border: 'none',
            background: 'transparent',
            color: 'var(--ds-ink-subtle)',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            '&:hover': { color: 'var(--ds-ink)' },
          }}
        >
          <Close sx={{ fontSize: 15 }} />
        </Box>
      )}
    </Root>
  )
}
